import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { Check, KeyboardArrowDown } from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import CompanyHouseCompanyCard from "./CompanyHouseCompanyCard";
import CompanyHouseNetworkGraph from "./CompanyHouseNetworkGraph";
import { formatCompanyHouseFacetValue } from "./companyHouseDisplay";
import CriteriaExtractionPanel, {
  buildBucketRows,
  createCountColumns,
} from "../../components/CriteriaExtractionPanel";
import {
  MultiSelectionAutocomplete,
  SingleSelectionAutocomplete,
} from "../../components/SelectionAutocomplete";
import {
  COMPANYHOUSE_EXPORT_FIELD_OPTIONS,
  DEFAULT_COMPANYHOUSE_EXPORT_FIELDS,
} from "../../helpers/exportFieldOptions";
import {
  EXPORT_FORMAT_OPTIONS,
  EXPORT_FORMAT_XLSX,
  getExportDownloadLabel,
  getExportFileExtension,
} from "../../helpers/exportFileFormats";
import { buildExportFilterRows } from "../../helpers/exportFilterSummary";

const PAGE_SIZE = 24;
const CH_BUCKET_CHART_HEIGHT = 360;
const SIDEBAR_CARD_SX = {
  p: 1.25,
  borderRadius: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(15, 23, 42, 0.06)",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};
const SIDEBAR_TITLE_SX = {
  mb: 0.85,
  fontWeight: 700,
  fontSize: "0.95rem",
  color: "#1f2937",
};
const SIDEBAR_ACCORDION_SX = {
  border: "1px solid rgba(15, 23, 42, 0.08)",
  borderRadius: "14px !important",
  backgroundColor: "#f8fafc",
  "&:before": { display: "none" },
};
const SIDEBAR_ACCORDION_SUMMARY_SX = {
  px: 1.25,
  minHeight: 46,
  "& .MuiAccordionSummary-content": {
    my: 0.75,
  },
};
const SIDEBAR_ACCORDION_DETAILS_SX = {
  display: "grid",
  gap: 1,
  px: 1.25,
  pt: 0,
  pb: 1.25,
};
const SIDEBAR_SECTION_LABEL_SX = {
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "#1f2937",
};
const SIDEBAR_SECTION_SUBTITLE_SX = {
  fontSize: "0.75rem",
  color: "#64748b",
};
const DATASET_LABELS = {
  crunchbase: "CB",
  globaldata: "GD",
  companyhouse: "CH",
};
const COMPANY_HOUSE_SORT_OPTIONS = [
  { value: "companyName", label: "Company Name" },
  { value: "companyNumber", label: "Company Number" },
  { value: "companyStatus", label: "Company Status" },
  { value: "companyType", label: "Company Type" },
  { value: "incorporatedOn", label: "Incorporated On" },
  { value: "extractedAtUtc", label: "Extracted At" },
];
const COMPANY_HOUSE_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];
const COMPANYHOUSE_MATCH_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "crunchbase_matched", label: "Matched to Crunchbase" },
  { value: "crunchbase_unmatched", label: "Unmatched to Crunchbase" },
  { value: "globaldata_matched", label: "Matched to GlobalData" },
  { value: "globaldata_unmatched", label: "Unmatched to GlobalData" },
  { value: "all_unmatched", label: "Unmatched (All)" },
];
const LIST_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "client", label: "Existing Client" },
  { value: "converted", label: "Converted +" },
  { value: "contacted", label: "Contacted" },
  { value: "not-client-anymore", label: "Not client anymore" },
  { value: "non-client", label: "Non-client" },
];
const COMPANYHOUSE_WORKSPACE_STORAGE_KEY = "companyhouse_workspace_state";
const EMPTY_ASSIGNMENT_FILTER = "__NO_ASSIGNED_COMPANIES__";
const COMPANY_LIST_VIEW_ALL = "ALL_COMPANIES";
const COMPANY_LIST_VIEW_ASSIGNMENTS = "ASSIGNMENTS";
const ENRICHMENT_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

function normalizeStoredArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeCompanyHouseMatchState(value) {
  return value === "matched" || value === "unmatched" ? value : "all";
}

function normalizeCompanyHouseRightPanelView(value) {
  return value === "companies" || value === "network" ? value : "visualisations";
}

function normalizeEnrichmentStatus(value) {
  return value === "yes" || value === "no" ? value : "all";
}

function loadStoredCompanyHouseWorkspaceConfig() {
  try {
    const raw = localStorage.getItem(COMPANYHOUSE_WORKSPACE_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function persistStoredCompanyHouseWorkspaceConfig(config) {
  try {
    localStorage.setItem(COMPANYHOUSE_WORKSPACE_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
}

function buildClearedCompanyHouseWorkspaceConfig() {
  return {
    rightPanelView: "visualisations",
    searchTerm: "",
    crunchbaseMatchState: "all",
    globaldataMatchState: "all",
    clientState: "all",
    companyListView: COMPANY_LIST_VIEW_ALL,
    selectedGraphCompanyNames: [],
    selectedStatuses: [],
    selectedCompanyTypes: [],
    selectedSicCodes: [],
    selectedIncorporationYears: [],
    selectedOfficerStatuses: [],
    selectedOfficerRoles: [],
    selectedPscStatuses: [],
    enrichmentStatus: "all",
    selectedEnrichmentConfidences: [],
    sortBy: "companyName",
    sortDirection: "asc",
  };
}

function normalizeClientState(value) {
  return value === "client" ||
    value === "converted" ||
    value === "contacted" ||
    value === "not-client-anymore" ||
    value === "non-client"
    ? value
    : "all";
}

function getClientStateFilterLabel(value) {
  if (value === "client") return "Existing client only";
  if (value === "converted") return "Converted + only";
  if (value === "contacted") return "Contacted only";
  if (value === "not-client-anymore") return "Not client anymore only";
  if (value === "non-client") return "Non-client only";
  return "All";
}

function getClientLifecycleStatusForClientState(value) {
  if (value === "client") return "EXISTING_CLIENT";
  if (value === "converted") return "CONVERTED_CLIENT";
  if (value === "not-client-anymore") return "NOT_CLIENT_ANYMORE";
  if (value === "non-client") return "NON_CLIENT";
  return null;
}

function hasMeaningfulValue(value) {
  return (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "" &&
    String(value).trim().toLowerCase() !== "unknown"
  );
}

function formatEnrichmentFacetValue(value) {
  return hasMeaningfulValue(value)
    ? String(value)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Unknown";
}

function formatCount(value) {
  return new Intl.NumberFormat("en-GB").format(Number(value || 0));
}

function buildChartData(buckets, limit = 8, facetType = "") {
  return (buckets || []).slice(0, limit).map((bucket) => ({
    label: formatCompanyHouseFacetValue(facetType, bucket.value),
    fullLabel: bucket.value,
    count: bucket.count,
  }));
}

function getActiveBucketEntry(state) {
  return state?.activePayload?.[0]?.payload || state?.payload || state || null;
}

function getBucketChartAxisAngle(chartData) {
  const longestLabelLength = chartData.reduce(
    (maxLength, entry) => Math.max(maxLength, entry.label?.length || 0),
    0
  );

  if (longestLabelLength > 22) return -45;
  return 0;
}

function buildFacetCountMap(buckets) {
  return new Map((buckets || []).map((bucket) => [bucket.value, bucket.count]));
}

function mergeFacetCountMaps(currentCounts, nextCounts) {
  const mergedCounts = new Map(currentCounts || []);
  (nextCounts || new Map()).forEach((count, value) => {
    mergedCounts.set(value, count);
  });
  return mergedCounts;
}

function buildFacetOptions(buckets, selectedValues) {
  const seen = new Set();
  const values = [];

  (buckets || []).forEach((bucket) => {
    const value = typeof bucket === "string" ? bucket : bucket?.value;
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    values.push(value);
  });

  (selectedValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    values.push(value);
  });

  return values;
}

function mergeFacetOptionValues(currentValues, nextValues) {
  const seen = new Set();
  const mergedValues = [];

  (currentValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    mergedValues.push(value);
  });

  (nextValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    mergedValues.push(value);
  });

  return mergedValues;
}

function getFacetValues(buckets) {
  return buildFacetOptions(buckets, [], null);
}

function SummaryCard({ label, value, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 3,
        backgroundColor: "#ffffff",
        color: "#0f172a",
        border: "1px solid rgba(15, 23, 42, 0.06)",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          mb: 0.45,
          fontWeight: 600,
          color: "#1f2937",
        }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
        {formatCount(value)}
      </Typography>
    </Paper>
  );
}

function BucketChart({
  title,
  buckets,
  emptyLabel,
  onSelect,
  facetType = "",
  color = "#1976d2",
  height = CH_BUCKET_CHART_HEIGHT,
  forceAxisAngle,
  yAxisLabel = "Count",
  yAxisWidth = 72,
  yAxisLabelOffset = 8,
}) {
  const chartData = useMemo(() => buildChartData(buckets, 8, facetType), [buckets, facetType]);
  const axisAngle =
    forceAxisAngle === undefined ? getBucketChartAxisAngle(chartData) : forceAxisAngle;

  const handleChartClick = (state) => {
    const payload = getActiveBucketEntry(state);
    if (payload?.fullLabel && onSelect) {
      onSelect(payload.fullLabel);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        {title}
      </Typography>

      {chartData.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>{emptyLabel}</Typography>
      ) : (
        <Box sx={{ width: "100%", height }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ left: 24, right: 16 }}
              onClick={handleChartClick}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                angle={forceAxisAngle === undefined ? axisAngle : forceAxisAngle}
                textAnchor={axisAngle === 0 ? "middle" : "end"}
                height={axisAngle === 0 ? 50 : 120}
                interval={0}
                tick={{ fontSize: 14 }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 14 }}
                width={yAxisWidth}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "left",
                  offset: yAxisLabelOffset,
                  style: { textAnchor: "middle", fontSize: 14, fill: "#475569" },
                }}
              />
              <Tooltip formatter={(value) => formatCount(value)} />
              <Legend wrapperStyle={{ fontSize: 14 }} />
              <Bar
                dataKey="count"
                name={title}
                fill={color}
                onClick={handleChartClick}
                cursor={onSelect ? "pointer" : "default"}
                activeBar={
                  onSelect ? { stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 } : undefined
                }
                minPointSize={onSelect ? 8 : 0}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

export default function CompanyHouseWorkspace({
  apiEnabled,
  postJson,
  deleteJson,
  postApiFile,
  getApiJson,
  initialQueryConfig,
  workspaceDataset,
  onWorkspaceDatasetChange,
  onOpenCrunchbaseMatch,
  onOpenGlobaldataMatch,
  getAssignmentForCompanyName,
  assignedCompanyNames = [],
  assignmentView = "MY_LEADS",
  setAssignmentView = () => {},
  assignmentsLoading = false,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  onAssignmentsChanged,
  dataRefreshToken = 0,
}) {
  const theme = useTheme();
  const useCompactRightPanelTabs = useMediaQuery(theme.breakpoints.down("sm"));
  const storedWorkspaceConfigRef = useRef(loadStoredCompanyHouseWorkspaceConfig());
  const storedWorkspaceConfig = storedWorkspaceConfigRef.current || {};
  const storedSearchTerm = storedWorkspaceConfig.searchTerm || "";
  const storedClientState = normalizeClientState(storedWorkspaceConfig.clientState);
  const storedCompanyListView = storedWorkspaceConfig.companyListView;
  const storedRightPanelView = normalizeCompanyHouseRightPanelView(
    storedWorkspaceConfig.rightPanelView
  );
  const [rightPanelView, setRightPanelView] = useState(() =>
    storedRightPanelView
  );
  const [workspaceDatasetMenuAnchor, setWorkspaceDatasetMenuAnchor] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => storedSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [crunchbaseMatchState, setCrunchbaseMatchState] = useState(() =>
    normalizeCompanyHouseMatchState(storedWorkspaceConfig.crunchbaseMatchState)
  );
  const [globaldataMatchState, setGlobaldataMatchState] = useState(() =>
    normalizeCompanyHouseMatchState(storedWorkspaceConfig.globaldataMatchState)
  );
  const [clientState, setClientState] = useState(() =>
    storedClientState
  );
  const [companyListView, setCompanyListView] = useState(() =>
    storedCompanyListView === COMPANY_LIST_VIEW_ASSIGNMENTS
      ? COMPANY_LIST_VIEW_ASSIGNMENTS
      : COMPANY_LIST_VIEW_ALL
  );
  const [selectedGraphCompanyNames, setSelectedGraphCompanyNames] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedGraphCompanyNames)
  );
  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedStatuses)
  );
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedCompanyTypes)
  );
  const [selectedSicCodes, setSelectedSicCodes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedSicCodes)
  );
  const [selectedIncorporationYears, setSelectedIncorporationYears] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedIncorporationYears)
  );
  const [selectedOfficerStatuses, setSelectedOfficerStatuses] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedOfficerStatuses)
  );
  const [selectedOfficerRoles, setSelectedOfficerRoles] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedOfficerRoles)
  );
  const [selectedPscStatuses, setSelectedPscStatuses] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedPscStatuses)
  );
  const [enrichmentStatus, setEnrichmentStatus] = useState(() =>
    normalizeEnrichmentStatus(storedWorkspaceConfig.enrichmentStatus)
  );
  const [selectedEnrichmentConfidences, setSelectedEnrichmentConfidences] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedEnrichmentConfidences)
  );
  const [sortBy, setSortBy] = useState(() => storedWorkspaceConfig.sortBy || "companyName");
  const [sortDirection, setSortDirection] = useState(() =>
    storedWorkspaceConfig.sortDirection === "desc" ? "desc" : "asc"
  );
  const [exportFileName, setExportFileName] = useState("company_house_export");
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMAT_XLSX);
  const [selectedExportFields, setSelectedExportFields] = useState(
    DEFAULT_COMPANYHOUSE_EXPORT_FIELDS
  );
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [facetOptionUniverse, setFacetOptionUniverse] = useState({
    statuses: [],
    companyTypes: [],
    sicCodes: [],
    incorporationYears: [],
    officerStatuses: [],
    officerRoles: [],
    pscStatuses: [],
    enrichmentConfidences: [],
  });
  const [facetCountUniverse, setFacetCountUniverse] = useState({
    statuses: new Map(),
    companyTypes: new Map(),
    sicCodes: new Map(),
    incorporationYears: new Map(),
    officerStatuses: new Map(),
    officerRoles: new Map(),
    pscStatuses: new Map(),
    enrichmentConfidences: new Map(),
  });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const searchRequestSequence = useRef(0);
  const initialQueryConfigAppliedRef = useRef(false);
  const initialQueryParamsRef = useRef(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams()
  );
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (
      initialQueryConfigAppliedRef.current ||
      initialQueryConfig?.workspaceDataset !== "companyhouse"
    ) {
      return;
    }

    const initialQueryParams = initialQueryParamsRef.current;
    const hasSearchParam =
      initialQueryParams.has("search") || initialQueryParams.has("company");
    const hasClientStateParam = initialQueryParams.has("clientState");
    const hasViewParam = initialQueryParams.has("view");

    setSearchTerm(
      hasSearchParam ? initialQueryConfig.searchTerm || "" : storedSearchTerm
    );
    setClientState(
      hasClientStateParam ? normalizeClientState(initialQueryConfig.clientState) : storedClientState
    );
    setCompanyListView(
      initialQueryConfig.companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS ||
        (hasClientStateParam
          ? initialQueryConfig.clientState
          : storedClientState) === "assigned" ||
        storedCompanyListView === COMPANY_LIST_VIEW_ASSIGNMENTS
        ? COMPANY_LIST_VIEW_ASSIGNMENTS
        : COMPANY_LIST_VIEW_ALL
    );
    setRightPanelView(
      hasViewParam
        ? normalizeCompanyHouseRightPanelView(initialQueryConfig.rightPanelView)
        : storedRightPanelView
    );
    initialQueryConfigAppliedRef.current = true;
  }, [initialQueryConfig, storedClientState, storedCompanyListView, storedRightPanelView, storedSearchTerm]);

  useEffect(() => {
    persistStoredCompanyHouseWorkspaceConfig({
      rightPanelView,
      searchTerm,
      crunchbaseMatchState,
      globaldataMatchState,
      clientState,
      companyListView,
      selectedGraphCompanyNames,
      selectedStatuses,
      selectedCompanyTypes,
      selectedSicCodes,
      selectedIncorporationYears,
      selectedOfficerStatuses,
      selectedOfficerRoles,
      selectedPscStatuses,
      enrichmentStatus,
      selectedEnrichmentConfidences,
      sortBy,
      sortDirection,
    });
  }, [
    clientState,
    companyListView,
    crunchbaseMatchState,
    enrichmentStatus,
    globaldataMatchState,
    rightPanelView,
    searchTerm,
    selectedCompanyTypes,
    selectedEnrichmentConfidences,
    selectedGraphCompanyNames,
    selectedIncorporationYears,
    selectedOfficerRoles,
    selectedOfficerStatuses,
    selectedPscStatuses,
    selectedSicCodes,
    selectedStatuses,
    sortBy,
    sortDirection,
  ]);

  const openWorkspaceDatasetMenu = (event) => {
    setWorkspaceDatasetMenuAnchor(event.currentTarget);
  };

  const closeWorkspaceDatasetMenu = () => {
    setWorkspaceDatasetMenuAnchor(null);
  };

  const resetAll = () => {
    const clearedWorkspaceConfig = buildClearedCompanyHouseWorkspaceConfig();
    persistStoredCompanyHouseWorkspaceConfig(clearedWorkspaceConfig);
    storedWorkspaceConfigRef.current = clearedWorkspaceConfig;
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCrunchbaseMatchState("all");
    setGlobaldataMatchState("all");
    setClientState("all");
    setCompanyListView(COMPANY_LIST_VIEW_ALL);
    setSelectedGraphCompanyNames([]);
    setSelectedStatuses([]);
    setSelectedCompanyTypes([]);
    setSelectedSicCodes([]);
    setSelectedIncorporationYears([]);
    setSelectedOfficerStatuses([]);
    setSelectedOfficerRoles([]);
    setSelectedPscStatuses([]);
    setEnrichmentStatus("all");
    setSelectedEnrichmentConfidences([]);
    setSortBy("companyName");
    setSortDirection("asc");
    setRightPanelView("visualisations");

    const params = new URLSearchParams({
      dataset: "companyhouse",
      view: "visualisations",
    });
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", nextUrl);
  };

  const requestBody = useMemo(
    () => ({
      search: debouncedSearchTerm,
      companyNames: (() => {
        const graphNames = selectedGraphCompanyNames.filter(Boolean);
        const assignmentNames =
          companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS ? assignedCompanyNames : [];

        if (companyListView !== COMPANY_LIST_VIEW_ASSIGNMENTS) {
          return graphNames;
        }

        if (graphNames.length === 0) {
          return assignmentNames.length > 0 ? assignmentNames : [EMPTY_ASSIGNMENT_FILTER];
        }

        const assignmentNameKeys = new Set(
          assignmentNames.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)
        );
        const intersection = graphNames.filter((value) =>
          assignmentNameKeys.has(String(value || "").trim().toLowerCase())
        );

        return intersection.length > 0 ? intersection : [EMPTY_ASSIGNMENT_FILTER];
      })(),
      matchedToCrunchbase:
        crunchbaseMatchState === "matched"
          ? true
          : crunchbaseMatchState === "unmatched"
          ? false
          : null,
      matchedToGlobaldata:
        globaldataMatchState === "matched"
          ? true
          : globaldataMatchState === "unmatched"
          ? false
          : null,
      matchedToClient: null,
      contacted: clientState === "contacted" ? true : null,
      clientLifecycleStatus: getClientLifecycleStatusForClientState(clientState),
      companyStatuses: selectedStatuses,
      companyTypes: selectedCompanyTypes,
      sicCodes: selectedSicCodes,
      incorporationYears: selectedIncorporationYears,
      officerStatuses: selectedOfficerStatuses,
      officerRoles: selectedOfficerRoles,
      pscStatuses: selectedPscStatuses,
      hasEnrichment:
        enrichmentStatus === "yes"
          ? true
          : enrichmentStatus === "no"
          ? false
          : null,
      enrichmentConfidences: selectedEnrichmentConfidences,
      sortBy,
      sortDirection,
      size: PAGE_SIZE,
    }),
    [
      debouncedSearchTerm,
      assignedCompanyNames,
      companyListView,
      selectedGraphCompanyNames,
      crunchbaseMatchState,
      globaldataMatchState,
      clientState,
      enrichmentStatus,
      selectedStatuses,
      selectedCompanyTypes,
      selectedEnrichmentConfidences,
      selectedSicCodes,
      selectedIncorporationYears,
      selectedOfficerStatuses,
      selectedOfficerRoles,
      selectedPscStatuses,
      sortBy,
      sortDirection,
    ]
  );

  const requestKey = JSON.stringify(requestBody);
  const matchedState =
    crunchbaseMatchState === "unmatched" && globaldataMatchState === "unmatched"
      ? "all_unmatched"
      : crunchbaseMatchState !== "all"
      ? `crunchbase_${crunchbaseMatchState}`
      : globaldataMatchState !== "all"
      ? `globaldata_${globaldataMatchState}`
      : "all";

  const handleContactedChanged = useCallback((companyName, contacted) => {
    const normalizedCompanyName = String(companyName || "").trim().toLowerCase();
    if (!normalizedCompanyName) {
      return;
    }

    setItems((currentItems) =>
      currentItems.map((company) =>
        String(company.companyName || "").trim().toLowerCase() === normalizedCompanyName
          ? { ...company, contacted }
          : company
      )
    );
  }, []);

  const applyGraphCompanyFilter = useCallback((companyNames) => {
    setSearchTerm("");
    const seen = new Set();
    setSelectedGraphCompanyNames(
      Array.isArray(companyNames)
        ? companyNames
            .map((value) => String(value || "").trim())
            .filter((value) => value !== "")
            .filter((value) => {
              const key = value.toLowerCase();
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
        : []
    );
  }, []);

  const loadSearchPage = useCallback(
    async (nextPage, append) => {
      if (!apiEnabled) return;

      const requestId = ++searchRequestSequence.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await postJson("/api/companyhouse/search", {
          ...requestBody,
          page: nextPage,
        });

        if (requestId !== searchRequestSequence.current) {
          return;
        }

        setItems((currentItems) =>
          append ? [...currentItems, ...(response.items || [])] : response.items || []
        );
        setTotal(response.total || 0);
        setSummary(response.summary || null);
        setPage(nextPage);
        setError("");
      } catch (requestError) {
        if (requestId !== searchRequestSequence.current) {
          return;
        }

        console.error("Unable to load Company House search results:", requestError);
        setError(requestError.message || "Unable to load Company House results.");
        if (!append) {
          setItems([]);
          setTotal(0);
          setSummary(null);
        }
      } finally {
        if (requestId !== searchRequestSequence.current) {
          return;
        }

        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [apiEnabled, postJson, requestBody]
  );

  useEffect(() => {
    if (
      initialQueryConfig?.workspaceDataset === "companyhouse" &&
      !initialQueryConfigAppliedRef.current
    ) {
      return;
    }

    loadSearchPage(0, false);
  }, [dataRefreshToken, initialQueryConfig?.workspaceDataset, loadSearchPage, requestKey]);

  useEffect(() => {
    if (!summary) return;

    setFacetOptionUniverse((currentUniverse) => ({
      statuses: mergeFacetOptionValues(
        currentUniverse.statuses,
        getFacetValues(summary.statusBuckets)
      ),
      companyTypes: mergeFacetOptionValues(
        currentUniverse.companyTypes,
        getFacetValues(summary.companyTypeBuckets)
      ),
      sicCodes: mergeFacetOptionValues(
        currentUniverse.sicCodes,
        getFacetValues(summary.sicBuckets)
      ),
      incorporationYears: mergeFacetOptionValues(
        currentUniverse.incorporationYears,
        getFacetValues(summary.incorporationYearBuckets)
      ),
      officerStatuses: mergeFacetOptionValues(
        currentUniverse.officerStatuses,
        getFacetValues(summary.officerStatusBuckets)
      ),
      officerRoles: mergeFacetOptionValues(
        currentUniverse.officerRoles,
        getFacetValues(summary.officerRoleBuckets)
      ),
      pscStatuses: mergeFacetOptionValues(
        currentUniverse.pscStatuses,
        getFacetValues(summary.pscStatusBuckets)
      ),
      enrichmentConfidences: mergeFacetOptionValues(
        currentUniverse.enrichmentConfidences,
        getFacetValues(summary.enrichmentConfidenceBuckets)
      ),
    }));
    setFacetCountUniverse((currentCounts) => ({
      statuses: mergeFacetCountMaps(
        currentCounts.statuses,
        buildFacetCountMap(summary.statusBuckets)
      ),
      companyTypes: mergeFacetCountMaps(
        currentCounts.companyTypes,
        buildFacetCountMap(summary.companyTypeBuckets)
      ),
      sicCodes: mergeFacetCountMaps(
        currentCounts.sicCodes,
        buildFacetCountMap(summary.sicBuckets)
      ),
      incorporationYears: mergeFacetCountMaps(
        currentCounts.incorporationYears,
        buildFacetCountMap(summary.incorporationYearBuckets)
      ),
      officerStatuses: mergeFacetCountMaps(
        currentCounts.officerStatuses,
        buildFacetCountMap(summary.officerStatusBuckets)
      ),
      officerRoles: mergeFacetCountMaps(
        currentCounts.officerRoles,
        buildFacetCountMap(summary.officerRoleBuckets)
      ),
      pscStatuses: mergeFacetCountMaps(
        currentCounts.pscStatuses,
        buildFacetCountMap(summary.pscStatusBuckets)
      ),
      enrichmentConfidences: mergeFacetCountMaps(
        currentCounts.enrichmentConfidences,
        buildFacetCountMap(summary.enrichmentConfidenceBuckets)
      ),
    }));
  }, [summary]);

  const facetData = useMemo(() => {
    const nextSummary = summary || {};
    const statusCounts = mergeFacetCountMaps(
      facetCountUniverse.statuses,
      buildFacetCountMap(nextSummary.statusBuckets)
    );
    const companyTypeCounts = mergeFacetCountMaps(
      facetCountUniverse.companyTypes,
      buildFacetCountMap(nextSummary.companyTypeBuckets)
    );
    const sicCounts = mergeFacetCountMaps(
      facetCountUniverse.sicCodes,
      buildFacetCountMap(nextSummary.sicBuckets)
    );
    const incorporationYearCounts = mergeFacetCountMaps(
      facetCountUniverse.incorporationYears,
      buildFacetCountMap(nextSummary.incorporationYearBuckets)
    );
    const officerStatusCounts = mergeFacetCountMaps(
      facetCountUniverse.officerStatuses,
      buildFacetCountMap(nextSummary.officerStatusBuckets)
    );
    const officerRoleCounts = mergeFacetCountMaps(
      facetCountUniverse.officerRoles,
      buildFacetCountMap(nextSummary.officerRoleBuckets)
    );
    const pscStatusCounts = mergeFacetCountMaps(
      facetCountUniverse.pscStatuses,
      buildFacetCountMap(nextSummary.pscStatusBuckets)
    );
    const enrichmentConfidenceCounts = mergeFacetCountMaps(
      facetCountUniverse.enrichmentConfidences,
      buildFacetCountMap(nextSummary.enrichmentConfidenceBuckets)
    );

    return {
      statuses: {
        options: buildFacetOptions(facetOptionUniverse.statuses, selectedStatuses, statusCounts),
        counts: statusCounts,
      },
      companyTypes: {
        options: buildFacetOptions(
          facetOptionUniverse.companyTypes,
          selectedCompanyTypes,
          companyTypeCounts
        ),
        counts: companyTypeCounts,
      },
      sicCodes: {
        options: buildFacetOptions(facetOptionUniverse.sicCodes, selectedSicCodes, sicCounts),
        counts: sicCounts,
      },
      incorporationYears: {
        options: buildFacetOptions(
          facetOptionUniverse.incorporationYears,
          selectedIncorporationYears,
          incorporationYearCounts
        ),
        counts: incorporationYearCounts,
      },
      officerStatuses: {
        options: buildFacetOptions(
          facetOptionUniverse.officerStatuses,
          selectedOfficerStatuses,
          officerStatusCounts
        ),
        counts: officerStatusCounts,
      },
      officerRoles: {
        options: buildFacetOptions(
          facetOptionUniverse.officerRoles,
          selectedOfficerRoles,
          officerRoleCounts
        ),
        counts: officerRoleCounts,
      },
      pscStatuses: {
        options: buildFacetOptions(
          facetOptionUniverse.pscStatuses,
          selectedPscStatuses,
          pscStatusCounts
        ),
        counts: pscStatusCounts,
      },
      enrichmentConfidences: {
        options: buildFacetOptions(
          facetOptionUniverse.enrichmentConfidences,
          selectedEnrichmentConfidences,
          enrichmentConfidenceCounts
        ),
        counts: enrichmentConfidenceCounts,
      },
    };
  }, [
    facetCountUniverse,
    facetOptionUniverse,
    selectedCompanyTypes,
    selectedEnrichmentConfidences,
    selectedIncorporationYears,
    selectedOfficerRoles,
    selectedOfficerStatuses,
    selectedPscStatuses,
    selectedSicCodes,
    selectedStatuses,
    summary,
  ]);

  const hasMore = items.length < total;
  const unmatchedAllSummaryCount = Math.max(
    0,
    (summary?.totalCompanies || 0) -
      (summary?.matchedToCrunchbaseCompanies || 0) -
      (summary?.matchedToGlobaldataCompanies || 0)
  );

  const enrichedItems = items;
  const companyHouseCriteriaExtractionSections = useMemo(() => {
    const makeRows = (buckets, facetType) =>
      buildBucketRows(
        (buckets || []).map((bucket) => ({
          value: formatCompanyHouseFacetValue(facetType, bucket?.value),
          count: bucket?.count,
        }))
      );
    const makeSection = ({
      id,
      title,
      subtitle,
      label,
      countLabel = "Companies",
      rows,
      emptyLabel,
      maxHeight = 260,
    }) => ({
      id,
      title,
      subtitle,
      columns: createCountColumns(label, countLabel),
      rows,
      emptyLabel,
      minHeight: 0,
      maxHeight,
    });

    return [
      makeSection({
        id: "company-status-distribution",
        title: "Company Status Distribution",
        subtitle: "Number of companies per Companies House status",
        label: "Company Status",
        rows: makeRows(summary?.statusBuckets, "companyStatus"),
        emptyLabel: "No company status distribution available for the selected criteria.",
      }),
      makeSection({
        id: "company-type-distribution",
        title: "Company Type Distribution",
        subtitle: "Number of companies per legal entity type",
        label: "Company Type",
        rows: makeRows(summary?.companyTypeBuckets, "companyType"),
        emptyLabel: "No company type distribution available for the selected criteria.",
      }),
      makeSection({
        id: "sic-distribution",
        title: "SIC Code Distribution",
        subtitle: "Number of companies per SIC code",
        label: "SIC Code",
        rows: makeRows(summary?.sicBuckets, "sic"),
        emptyLabel: "No SIC code distribution available for the selected criteria.",
        maxHeight: 320,
      }),
      makeSection({
        id: "incorporation-year-distribution",
        title: "Incorporation Year Distribution",
        subtitle: "Number of companies by incorporation year",
        label: "Incorporation Year",
        rows: makeRows(summary?.incorporationYearBuckets, "incorporationYear"),
        emptyLabel: "No incorporation year distribution available for the selected criteria.",
        maxHeight: 320,
      }),
    ];
  }, [summary]);

  const companyHouseExportFilters = useMemo(
    () =>
      buildExportFilterRows("Company House", [
        {
          label: "Match Status",
          value:
            matchedState === "crunchbase_matched"
              ? "Matched to Crunchbase only"
              : matchedState === "crunchbase_unmatched"
              ? "Unmatched to Crunchbase only"
              : matchedState === "globaldata_matched"
              ? "Matched to GlobalData only"
              : matchedState === "globaldata_unmatched"
              ? "Unmatched to GlobalData only"
              : matchedState === "all_unmatched"
              ? "Unmatched across all datasets"
              : "",
        },
        {
          label: "List Status",
          value: clientState !== "all" ? getClientStateFilterLabel(clientState) : "",
        },
        {
          label: "Assignments",
          value: companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS ? "Assignments" : "",
        },
        { label: "Search", value: debouncedSearchTerm },
        { label: "Company", value: selectedGraphCompanyNames },
        {
          label: "Status",
          value: selectedStatuses.map((value) => formatCompanyHouseFacetValue("companyStatus", value)),
        },
        {
          label: "Type",
          value: selectedCompanyTypes.map((value) => formatCompanyHouseFacetValue("companyType", value)),
        },
        { label: "SIC", value: selectedSicCodes },
        { label: "Incorporation Year", value: selectedIncorporationYears },
        {
          label: "Officer Status",
          value: selectedOfficerStatuses.map((value) =>
            formatCompanyHouseFacetValue("officerStatus", value)
          ),
        },
        {
          label: "Officer Role",
          value: selectedOfficerRoles.map((value) =>
            formatCompanyHouseFacetValue("officerRole", value)
          ),
        },
        {
          label: "PSC Status",
          value: selectedPscStatuses.map((value) =>
            formatCompanyHouseFacetValue("pscStatus", value)
          ),
        },
        {
          label: "Enriched Data",
          value: enrichmentStatus === "yes" ? "Yes" : enrichmentStatus === "no" ? "No" : "",
        },
        {
          label: "Enrichment Confidence",
          value: selectedEnrichmentConfidences.map(formatEnrichmentFacetValue),
        },
      ]),
    [
      clientState,
      companyListView,
      debouncedSearchTerm,
      enrichmentStatus,
      matchedState,
      selectedCompanyTypes,
      selectedEnrichmentConfidences,
      selectedGraphCompanyNames,
      selectedIncorporationYears,
      selectedOfficerRoles,
      selectedOfficerStatuses,
      selectedPscStatuses,
      selectedSicCodes,
      selectedStatuses,
    ]
  );

  const exportCurrentSearchToWorkbook = useCallback(async () => {
    if (!apiEnabled || exporting || typeof postApiFile !== "function") return;

    setExporting(true);

    try {
      await postApiFile(
        "/api/companyhouse/export",
        {
          search: requestBody,
          selectedFields: selectedExportFields,
          exportFilters: companyHouseExportFilters,
          fileName: exportFileName,
          format: exportFormat,
        },
        `${exportFileName || "company_house_export"}.${getExportFileExtension(exportFormat)}`
      );
    } catch (exportError) {
      console.error("Unable to export Company House workbook:", exportError);
      setError(exportError.message || "Unable to export Company House workbook.");
    } finally {
      setExporting(false);
    }
  }, [
    apiEnabled,
    exportFileName,
    exportFormat,
    exporting,
    companyHouseExportFilters,
    postApiFile,
    requestBody,
    selectedExportFields,
  ]);

  const handleBucketSelection = (filterType, value) => {
    if (!value) return;

    if (filterType === "statuses") {
      setSelectedStatuses((current) =>
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      );
      return;
    }

    if (filterType === "companyTypes") {
      setSelectedCompanyTypes((current) =>
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      );
      return;
    }

    if (filterType === "sicCodes") {
      setSelectedSicCodes((current) =>
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      );
      return;
    }

    if (filterType === "incorporationYears") {
      setSelectedIncorporationYears((current) =>
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      );
    }
  };

  if (!apiEnabled) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Company House requires API mode because this workspace is backed by the new server-side
        search endpoint.
      </Alert>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: { xs: "static", md: "fixed" },
          top: { md: 76 },
          left: { md: 0 },
          right: { md: 0 },
          zIndex: 12,
          mb: 0.25,
        }}
      >
        <Paper
          sx={{
            py: 1,
            borderRadius: 0,
            backgroundColor: "#ffffff",
            border: "none",
            boxShadow: "none",
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: 1, md: 1.5 },
                flexWrap: "nowrap",
                overflowX: "auto",
                width: "100%",
                px: { xs: 0, md: 1.5 },
              }}
            >
              <Typography
                sx={{
                  display: "block",
                  color: "#94a3b8",
                  fontSize: { xs: "0.72rem", md: "0.8rem" },
                  fontWeight: 700,
                  letterSpacing: { xs: "0.05em", md: "0.08em" },
                  textTransform: "uppercase",
                  pl: { xs: 0, md: 1.5 },
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Workspace View
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  marginLeft: "auto",
                  gap: 1,
                  width: "fit-content",
                  maxWidth: "100%",
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  endIcon={<KeyboardArrowDown fontSize="small" />}
                  onClick={openWorkspaceDatasetMenu}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    px: { xs: 0.8, md: 1.5 },
                    minWidth: { xs: 82, md: 128 },
                    fontSize: { xs: "0.74rem", md: "0.88rem" },
                  }}
                >
                  {useCompactRightPanelTabs
                    ? `DS: ${DATASET_LABELS[workspaceDataset] || "CB"}`
                    : `Data Source: ${DATASET_LABELS[workspaceDataset] || "CB"}`}
                </Button>
                <Tabs
                  data-right-panel-tabs="true"
                  value={rightPanelView}
                  onChange={(_, value) => setRightPanelView(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    flex: "0 0 auto",
                    display: "inline-flex",
                    minWidth: 0,
                    flexShrink: 0,
                    minHeight: "auto",
                    width: "fit-content",
                    maxWidth: "100%",
                    "& .MuiTabs-scroller": {
                      overflow: "visible !important",
                    },
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .MuiTabs-flexContainer": {
                      gap: 0.5,
                      flexWrap: "nowrap",
                    },
                    "& .MuiTab-root": {
                      minHeight: { xs: 34, md: 38 },
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: { xs: "0.74rem", md: "0.88rem" },
                      borderRadius: 999,
                      px: { xs: 0.9, md: 1.6 },
                      py: 0.5,
                      color: "#475569",
                      minWidth: { xs: 92, md: 136 },
                    },
                    "& .Mui-selected": {
                      color: "#0f172a !important",
                      backgroundColor: "#eef2f7",
                    },
                  }}
                >
                  <Tab
                    label={useCompactRightPanelTabs ? "Charts" : "Visualisations"}
                    value="visualisations"
                  />
                  <Tab
                    label="Network"
                    value="network"
                  />
                  <Tab
                    label={useCompactRightPanelTabs ? "List" : "Company List"}
                    value="companies"
                  />
                </Tabs>
                <Menu
                  anchorEl={workspaceDatasetMenuAnchor}
                  open={Boolean(workspaceDatasetMenuAnchor)}
                  onClose={closeWorkspaceDatasetMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      borderRadius: 2,
                      boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
                    },
                  }}
                >
                  <MenuItem
                    selected={workspaceDataset === "crunchbase"}
                    onClick={() => {
                      onWorkspaceDatasetChange?.("crunchbase");
                      closeWorkspaceDatasetMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {workspaceDataset === "crunchbase" ? <Check fontSize="small" /> : null}
                    </ListItemIcon>
                    <ListItemText primary="Crunchbase" />
                  </MenuItem>
                  <MenuItem
                    selected={workspaceDataset === "globaldata"}
                    onClick={() => {
                      onWorkspaceDatasetChange?.("globaldata");
                      closeWorkspaceDatasetMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {workspaceDataset === "globaldata" ? <Check fontSize="small" /> : null}
                    </ListItemIcon>
                    <ListItemText primary="GlobalData" />
                  </MenuItem>
                  <MenuItem
                    selected={workspaceDataset === "companyhouse"}
                    onClick={() => {
                      onWorkspaceDatasetChange?.("companyhouse");
                      closeWorkspaceDatasetMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {workspaceDataset === "companyhouse" ? <Check fontSize="small" /> : null}
                    </ListItemIcon>
                    <ListItemText primary="Company House" />
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Container>
        </Paper>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "340px minmax(0, 1fr)" },
          gap: { xs: 1.5, md: 2, lg: 2.5 },
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "none", lg: 340 },
            justifySelf: { xs: "stretch", lg: "center" },
            alignSelf: { lg: "start" },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 1,
              position: { xs: "static", lg: "fixed" },
              top: { lg: 152 },
              left: { lg: "max(24px, calc((100vw - 1536px) / 2 + 24px))" },
              width: { xs: "100%", lg: 340 },
              maxWidth: { lg: 340 },
              boxSizing: "border-box",
              alignContent: { lg: "start" },
              maxHeight: { lg: "calc(100vh - 188px)" },
              overflowY: { lg: "auto" },
              pr: { lg: 0.5 },
              scrollbarGutter: { lg: "stable" },
            }}
          >
            <Paper sx={SIDEBAR_CARD_SX}>
              <Typography sx={SIDEBAR_TITLE_SX}>Match Status</Typography>
              <SingleSelectionAutocomplete
                label="Match Status"
                value={matchedState}
                onChange={(value) => {
                  setCrunchbaseMatchState("all");
                  setGlobaldataMatchState("all");

                  if (value === "crunchbase_matched") {
                    setCrunchbaseMatchState("matched");
                  } else if (value === "crunchbase_unmatched") {
                    setCrunchbaseMatchState("unmatched");
                  } else if (value === "globaldata_matched") {
                    setGlobaldataMatchState("matched");
                  } else if (value === "globaldata_unmatched") {
                    setGlobaldataMatchState("unmatched");
                  } else if (value === "all_unmatched") {
                    setCrunchbaseMatchState("unmatched");
                    setGlobaldataMatchState("unmatched");
                  }
                }}
                options={COMPANYHOUSE_MATCH_STATUS_OPTIONS}
              />
            </Paper>

            <Paper sx={SIDEBAR_CARD_SX}>
              <Typography sx={SIDEBAR_TITLE_SX}>List Status</Typography>
              <SingleSelectionAutocomplete
                label="List Status"
                value={clientState}
                onChange={(value) => setClientState(value || "all")}
                options={LIST_STATUS_OPTIONS}
              />
            </Paper>

            <Paper
              sx={{
                p: 0.5,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Tabs
                value={companyListView}
                onChange={(event, nextValue) => setCompanyListView(nextValue)}
                orientation="vertical"
                variant="scrollable"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight: "auto",
                  "& .MuiTabs-flexContainer": {
                    alignItems: "stretch",
                  },
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    textAlign: "left",
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    borderRadius: 2,
                    mx: 0.5,
                    px: 1,
                    py: 0.5,
                  },
                }}
              >
                <Tab label="All Companies" value={COMPANY_LIST_VIEW_ALL} />
                <Tab label="Assignments" value={COMPANY_LIST_VIEW_ASSIGNMENTS} />
              </Tabs>
            </Paper>

            {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS && (
              <Paper sx={SIDEBAR_CARD_SX}>
                <Typography sx={SIDEBAR_TITLE_SX}>Assignment View</Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={assignmentView}
                      label="Scope"
                      onChange={(event) => setAssignmentView(event.target.value)}
                    >
                      <MenuItem value="MY_LEADS">My Leads</MenuItem>
                      <MenuItem value="ALL" disabled={!canManageAssignments}>
                        All Assignments
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {assignmentsLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        Loading assignments...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                      {assignmentView === "ALL" && canManageAssignments
                        ? "Shows every currently assigned company."
                        : "Shows companies assigned to your user."}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            <Paper sx={SIDEBAR_CARD_SX}>
              <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                >
                  <Box>
                    <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Export</Typography>
                    <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                      {selectedExportFields.length} fields selected
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                  <TextField
                    size="small"
                    label="File Name"
                    value={exportFileName}
                    onChange={(event) => setExportFileName(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FileDownloadOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small">
                    <InputLabel id="companyhouse-export-format-label">Format</InputLabel>
                    <Select
                      labelId="companyhouse-export-format-label"
                      label="Format"
                      value={exportFormat}
                      onChange={(event) => setExportFormat(event.target.value)}
                    >
                      {EXPORT_FORMAT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MultiSelectionAutocomplete
                    label="Fields"
                    values={selectedExportFields}
                    onChange={setSelectedExportFields}
                    options={COMPANYHOUSE_EXPORT_FIELD_OPTIONS}
                    showCheckboxes
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={exportCurrentSearchToWorkbook}
                    disabled={exporting || selectedExportFields.length === 0}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      boxShadow: "none",
                    }}
                  >
                    {exporting ? "Exporting..." : `Download ${getExportDownloadLabel(exportFormat)}`}
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper sx={{ ...SIDEBAR_CARD_SX, mt: 0.75 }}>
              <Typography sx={SIDEBAR_TITLE_SX}>Search</Typography>
              <TextField
                label="Company search"
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm.trim() && !isSearchFocused ? "" : searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder={searchTerm.trim() && !isSearchFocused ? "" : "Search company name or number"}
                InputProps={{
                  startAdornment:
                    searchTerm.trim() && !isSearchFocused ? (
                      <InputAdornment position="start">
                        <Chip
                          label={searchTerm.trim()}
                          onDelete={() => setSearchTerm("")}
                          onMouseDown={(event) => event.preventDefault()}
                        />
                      </InputAdornment>
                    ) : undefined,
                }}
              />
              {selectedGraphCompanyNames.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                  {selectedGraphCompanyNames.map((value) => (
                    <Chip
                      key={`graph-company-${value}`}
                      label={`Company: ${value}`}
                      onDelete={() =>
                        setSelectedGraphCompanyNames((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                </Box>
              ) : null}
            </Paper>

            <Paper sx={SIDEBAR_CARD_SX}>
              <Typography sx={SIDEBAR_TITLE_SX}>Filters</Typography>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Company filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Status, type, SIC, and incorporation year
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <MultiSelectionAutocomplete
                      label="Company Status"
                      values={selectedStatuses}
                      onChange={setSelectedStatuses}
                      options={facetData.statuses.options}
                      counts={facetData.statuses.counts}
                      formatOptionLabel={(value) =>
                        formatCompanyHouseFacetValue("companyStatus", value)
                      }
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Company Type"
                      values={selectedCompanyTypes}
                      onChange={setSelectedCompanyTypes}
                      options={facetData.companyTypes.options}
                      counts={facetData.companyTypes.counts}
                      formatOptionLabel={(value) =>
                        formatCompanyHouseFacetValue("companyType", value)
                      }
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="SIC Codes"
                      values={selectedSicCodes}
                      onChange={setSelectedSicCodes}
                      options={facetData.sicCodes.options}
                      counts={facetData.sicCodes.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Incorporation Year"
                      values={selectedIncorporationYears}
                      onChange={setSelectedIncorporationYears}
                      options={facetData.incorporationYears.options}
                      counts={facetData.incorporationYears.counts}
                      showCheckboxes
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>People filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Officer and Persons of Significant Control filters
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <MultiSelectionAutocomplete
                      label="Officer Status"
                      values={selectedOfficerStatuses}
                      onChange={setSelectedOfficerStatuses}
                      options={facetData.officerStatuses.options}
                      counts={facetData.officerStatuses.counts}
                      formatOptionLabel={(value) =>
                        formatCompanyHouseFacetValue("officerStatus", value)
                      }
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Officer Role"
                      values={selectedOfficerRoles}
                      onChange={setSelectedOfficerRoles}
                      options={facetData.officerRoles.options}
                      counts={facetData.officerRoles.counts}
                      formatOptionLabel={(value) =>
                        formatCompanyHouseFacetValue("officerRole", value)
                      }
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Persons of Significant Control Status"
                      values={selectedPscStatuses}
                      onChange={setSelectedPscStatuses}
                      options={facetData.pscStatuses.options}
                      counts={facetData.pscStatuses.counts}
                      formatOptionLabel={(value) =>
                        formatCompanyHouseFacetValue("pscStatus", value)
                      }
                      showCheckboxes
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Enrichment filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Enriched data availability and confidence
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <SingleSelectionAutocomplete
                      label="Has Enriched Data"
                      value={enrichmentStatus}
                      onChange={setEnrichmentStatus}
                      options={ENRICHMENT_STATUS_OPTIONS}
                    />
                    <MultiSelectionAutocomplete
                      label="Confidence"
                      values={selectedEnrichmentConfidences}
                      onChange={setSelectedEnrichmentConfidences}
                      options={facetData.enrichmentConfidences.options}
                      counts={facetData.enrichmentConfidences.counts}
                      formatOptionLabel={formatEnrichmentFacetValue}
                      showCheckboxes
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>

            <Paper sx={SIDEBAR_CARD_SX}>
              <Box sx={{ width: "100%", display: "grid", gap: 1 }}>
                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1f2937" }}>
                        Sorting
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                        Ordering and display direction
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ ...SIDEBAR_ACCORDION_DETAILS_SX, gap: 1.25 }}>
                    <SingleSelectionAutocomplete
                      label="Sort By"
                      value={sortBy}
                      onChange={setSortBy}
                      options={COMPANY_HOUSE_SORT_OPTIONS}
                    />
                    <SingleSelectionAutocomplete
                      label="Direction"
                      value={sortDirection}
                      onChange={setSortDirection}
                      options={COMPANY_HOUSE_DIRECTION_OPTIONS}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>

            <Paper sx={SIDEBAR_CARD_SX}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={resetAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 999,
                }}
              >
                Reset Filters
              </Button>
            </Paper>
          </Box>
        </Box>

        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && !summary ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid rgba(15, 23, 42, 0.06)",
                textAlign: "center",
              }}
            >
              <CircularProgress size={30} />
              <Typography sx={{ mt: 1.25, color: "#64748b" }}>
                Loading Company House workspace...
              </Typography>
            </Paper>
          ) : rightPanelView === "visualisations" ? (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", xl: "repeat(4, minmax(0, 1fr))" },
                  gap: 1.25,
                }}
              >
                <SummaryCard label="Companies" value={summary?.totalCompanies || 0} />
                <SummaryCard
                  label="Matched to Crunchbase"
                  value={summary?.matchedToCrunchbaseCompanies || 0}
                  onClick={() => {
                    setCrunchbaseMatchState("matched");
                    setGlobaldataMatchState("all");
                  }}
                />
                <SummaryCard
                  label="Matched to GlobalData"
                  value={summary?.matchedToGlobaldataCompanies || 0}
                  onClick={() => {
                    setCrunchbaseMatchState("all");
                    setGlobaldataMatchState("matched");
                  }}
                />
                <SummaryCard
                  label="Existing Clients"
                  value={summary?.existingClientCompanies || 0}
                  onClick={() => setClientState("client")}
                />
                <SummaryCard
                  label="Unmatched Total"
                  value={unmatchedAllSummaryCount}
                  onClick={() => {
                    setCrunchbaseMatchState("unmatched");
                    setGlobaldataMatchState("unmatched");
                  }}
                />
                <SummaryCard
                  label="Unmatched to Crunchbase"
                  value={summary?.unmatchedToCrunchbaseCompanies || 0}
                  onClick={() => {
                    setCrunchbaseMatchState("unmatched");
                    setGlobaldataMatchState("all");
                  }}
                />
                <SummaryCard
                  label="Unmatched to GlobalData"
                  value={summary?.unmatchedToGlobaldataCompanies || 0}
                  onClick={() => {
                    setCrunchbaseMatchState("all");
                    setGlobaldataMatchState("unmatched");
                  }}
                />
                <SummaryCard
                  label="Non-client"
                  value={summary?.nonClientCompanies || 0}
                  onClick={() => setClientState("non-client")}
                />
              </Box>

              <CriteriaExtractionPanel
                title="Company House Criteria Extraction"
                subtitle="Tabular company and officer summaries for the active Company House criteria."
                summaryText={`${formatCount(summary?.totalCompanies || 0)} companies, ${formatCount(
                  summary?.totalOfficers || 0
                )} officers, ${formatCount(summary?.totalPscs || 0)} PSCs`}
                sections={companyHouseCriteriaExtractionSections}
                defaultFileName="company_house_criteria_extraction"
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1.5,
                }}
              >
                <BucketChart
                  title="Company Status"
                  buckets={summary?.statusBuckets}
                  emptyLabel="No company status distribution available."
                  onSelect={(value) => handleBucketSelection("statuses", value)}
                  facetType="companyStatus"
                  color="#0f766e"
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="Company Type"
                  buckets={summary?.companyTypeBuckets}
                  emptyLabel="No company type distribution available."
                  onSelect={(value) => handleBucketSelection("companyTypes", value)}
                  facetType="companyType"
                  color="#1976d2"
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="SIC Codes"
                  buckets={summary?.sicBuckets}
                  emptyLabel="No SIC distribution available."
                  onSelect={(value) => handleBucketSelection("sicCodes", value)}
                  color="#6d28d9"
                  forceAxisAngle={-45}
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="Incorporation Year"
                  buckets={summary?.incorporationYearBuckets}
                  emptyLabel="No incorporation year distribution available."
                  onSelect={(value) => handleBucketSelection("incorporationYears", value)}
                  color="#d97706"
                  yAxisLabel="Company Count"
                />
              </Box>

              <Box sx={{ mt: 0.5 }}>
                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Active Filter Snapshot
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {matchedState !== "all" && (
                    <Chip
                      label={
                        matchedState === "crunchbase_matched"
                          ? "Matched to Crunchbase only"
                          : matchedState === "crunchbase_unmatched"
                          ? "Unmatched to Crunchbase only"
                          : matchedState === "globaldata_matched"
                          ? "Matched to GlobalData only"
                          : matchedState === "globaldata_unmatched"
                          ? "Unmatched to GlobalData only"
                          : "Unmatched across all datasets"
                      }
                      onDelete={() => {
                        setCrunchbaseMatchState("all");
                        setGlobaldataMatchState("all");
                      }}
                    />
                  )}
                  {clientState !== "all" && (
                    <Chip
                      label={getClientStateFilterLabel(clientState)}
                      onDelete={() => setClientState("all")}
                    />
                  )}
                  {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS && (
                    <Chip
                      label="Assignments"
                      onDelete={() => setCompanyListView(COMPANY_LIST_VIEW_ALL)}
                    />
                  )}
                  {debouncedSearchTerm && (
                    <Chip label={`Search: ${debouncedSearchTerm}`} onDelete={() => setSearchTerm("")} />
                  )}
                  {selectedGraphCompanyNames.map((value) => (
                    <Chip
                      key={`graph-company-snapshot-${value}`}
                      label={`Company: ${value}`}
                      onDelete={() =>
                        setSelectedGraphCompanyNames((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedStatuses.map((value) => (
                    <Chip
                      key={`status-${value}`}
                      label={`Status: ${formatCompanyHouseFacetValue("companyStatus", value)}`}
                      onDelete={() =>
                        setSelectedStatuses((current) => current.filter((entry) => entry !== value))
                      }
                    />
                  ))}
                  {selectedCompanyTypes.map((value) => (
                    <Chip
                      key={`type-${value}`}
                      label={`Type: ${formatCompanyHouseFacetValue("companyType", value)}`}
                      onDelete={() =>
                        setSelectedCompanyTypes((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedSicCodes.map((value) => (
                    <Chip
                      key={`sic-${value}`}
                      label={`SIC: ${value}`}
                      onDelete={() =>
                        setSelectedSicCodes((current) => current.filter((entry) => entry !== value))
                      }
                    />
                  ))}
                  {selectedIncorporationYears.map((value) => (
                    <Chip
                      key={`incorporation-year-${value}`}
                      label={`Incorporation Year: ${value}`}
                      onDelete={() =>
                        setSelectedIncorporationYears((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedOfficerStatuses.map((value) => (
                    <Chip
                      key={`officer-status-${value}`}
                      label={`Officer Status: ${formatCompanyHouseFacetValue("officerStatus", value)}`}
                      onDelete={() =>
                        setSelectedOfficerStatuses((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedOfficerRoles.map((value) => (
                    <Chip
                      key={`officer-role-${value}`}
                      label={`Officer Role: ${formatCompanyHouseFacetValue("officerRole", value)}`}
                      onDelete={() =>
                        setSelectedOfficerRoles((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedPscStatuses.map((value) => (
                    <Chip
                      key={`psc-status-${value}`}
                      label={`Persons of Significant Control Status: ${formatCompanyHouseFacetValue("pscStatus", value)}`}
                      onDelete={() =>
                        setSelectedPscStatuses((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {enrichmentStatus !== "all" ? (
                    <Chip
                      key="enrichment-status"
                      label={`Enriched Data: ${enrichmentStatus === "yes" ? "Yes" : "No"}`}
                      onDelete={() => setEnrichmentStatus("all")}
                    />
                  ) : null}
                  {selectedEnrichmentConfidences.map((value) => (
                    <Chip
                      key={`enrichment-confidence-${value}`}
                      label={`Confidence: ${formatEnrichmentFacetValue(value)}`}
                      onDelete={() =>
                        setSelectedEnrichmentConfidences((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {!debouncedSearchTerm &&
                    selectedGraphCompanyNames.length === 0 &&
                    crunchbaseMatchState === "all" &&
                    globaldataMatchState === "all" &&
                    clientState === "all" &&
                    companyListView === COMPANY_LIST_VIEW_ALL &&
                    selectedStatuses.length === 0 &&
                    selectedCompanyTypes.length === 0 &&
                    selectedSicCodes.length === 0 &&
                    selectedIncorporationYears.length === 0 &&
                    selectedOfficerStatuses.length === 0 &&
                    selectedOfficerRoles.length === 0 &&
                    selectedPscStatuses.length === 0 &&
                    enrichmentStatus === "all" &&
                    selectedEnrichmentConfidences.length === 0 &&
                    (
                      <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                        No filters applied. Charts and cards reflect the current Company House
                        search universe.
                      </Typography>
                    )}
                </Box>
              </Box>
            </Box>
          ) : rightPanelView === "network" ? (
            <CompanyHouseNetworkGraph
              apiEnabled={apiEnabled}
              postJson={postJson}
              searchRequest={requestBody}
              setSidebarCompanyNames={applyGraphCompanyFilter}
            />
          ) : (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                Total Company House companies: {formatCount(total)}
              </Typography>

              {items.length === 0 && !loading ? (
                <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                  {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS
                    ? "No assigned Company House companies match the current filters."
                    : "No Company House companies match the current filters."}
                </Typography>
              ) : (
                enrichedItems.map((company) => (
                  <CompanyHouseCompanyCard
                    key={`${company.companyNumber || company.companyName}-${company.extractedAtUtc || ""}`}
                    company={company}
                    postJson={postJson}
                    deleteJson={deleteJson}
                    getApiJson={getApiJson}
                    onOpenCrunchbaseMatch={onOpenCrunchbaseMatch}
                    onOpenGlobaldataMatch={onOpenGlobaldataMatch}
                    selectedOfficerStatuses={selectedOfficerStatuses}
                    selectedOfficerRoles={selectedOfficerRoles}
                    selectedPscStatuses={selectedPscStatuses}
                    assignment={getAssignmentForCompanyName?.(company.companyName) || null}
                    canManageAssignments={canManageAssignments}
                    assignableUsers={assignableUsers}
                    assignableUsersLoading={assignableUsersLoading}
                    onAssignmentsChanged={onAssignmentsChanged}
                    onContactedChanged={handleContactedChanged}
                  />
                ))
              )}

              {hasMore && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => loadSearchPage(page + 1, true)}
                    disabled={loadingMore}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
